import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { geofencesApi, type GeofenceQuery } from '@/lib/api/endpoints';
import { queryKeys } from './keys';
import type { CreateGeofenceInput, Id } from '@/types/api';

export function useGeofences(query?: GeofenceQuery) {
  return useQuery({
    queryKey: queryKeys.geofences.list(query),
    queryFn: () => geofencesApi.list(query),
    placeholderData: (previous) => previous,
  });
}

/** Map overlay: only active fences, and enough of them to cover a fleet. */
export function useActiveGeofences(enabled = true) {
  return useQuery({
    queryKey: queryKeys.geofences.list({ isActive: true, limit: 200 }),
    queryFn: () => geofencesApi.list({ isActive: true, limit: 200 }),
    enabled,
    staleTime: 120_000,
  });
}

export function useGeofence(id: Id | null | undefined) {
  return useQuery({
    queryKey: queryKeys.geofences.detail(id ?? ''),
    queryFn: () => geofencesApi.get(id as Id),
    enabled: Boolean(id),
  });
}

export function useCreateGeofence() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGeofenceInput) => geofencesApi.create(input),
    onSuccess: (fence) => {
      client.invalidateQueries({ queryKey: queryKeys.geofences.all });
      toast.success(`Geofence "${fence.name}" created`);
    },
  });
}

export function useUpdateGeofence() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: Id; input: Partial<CreateGeofenceInput> }) =>
      geofencesApi.update(id, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.geofences.all });
    },
  });
}

export function useDeleteGeofence() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => geofencesApi.remove(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.geofences.all });
      toast.success('Geofence deleted');
    },
  });
}
